/**
 * EducationalRibbon.jsx — Feb 2026
 *
 * Reusable, prominent "Educational only — NOT a medical device" ribbon.
 * Reinforces Nutramilo's strict regulatory positioning at the key surfaces
 * where clinical-looking data appears:
 *
 *   • BHI Dashboard header
 *   • Auto-diagnosis suggestions panel
 *   • PDF / print-friendly export
 *   • Twin Network page
 *
 * Variants:
 *   - "banner"    → prominent full-width gradient card (default, use at page top)
 *   - "inline"    → compact one-line pill inside a panel
 *   - "print"     → high-contrast border ribbon optimised for PDF/print
 */
import React from "react";
import { ShieldAlert, GraduationCap } from "lucide-react";

const COPY = {
  bg: {
    tag: "Образователна платформа",
    body: "Nutramilo НЕ е медицинско изделие. Съдържанието е за образователни цели и не замества консултация с лекар, диагностика или лечение.",
    short: "Образователна платформа — НЕ е медицинско изделие.",
  },
  en: {
    tag: "Educational platform",
    body: "Nutramilo is NOT a medical device. Content is provided for educational purposes and does not replace medical consultation, diagnosis or treatment.",
    short: "Educational platform — NOT a medical device.",
  },
};

export default function EducationalRibbon({
  lang = "bg",
  variant = "banner",
  isDark = false,
  className = "",
  testId = "educational-ribbon",
}) {
  const t = COPY[lang] || COPY.bg;

  if (variant === "inline") {
    return (
      <div
        data-testid={testId}
        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold leading-tight ${
          isDark
            ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
            : "border-amber-300 bg-amber-50 text-amber-800"
        } ${className}`}
      >
        <GraduationCap className="w-3.5 h-3.5 shrink-0" />
        <span>{t.short}</span>
      </div>
    );
  }

  if (variant === "print") {
    return (
      <div
        data-testid={testId}
        className={`rounded-lg border-2 border-amber-500 bg-amber-50 px-4 py-3 text-amber-900 ${className}`}
      >
        <div className="flex items-start gap-2">
          <ShieldAlert className="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
          <div>
            <div className="text-[11px] font-extrabold uppercase tracking-wide">
              {t.tag}
            </div>
            <p className="text-xs leading-snug mt-0.5">{t.body}</p>
          </div>
        </div>
      </div>
    );
  }

  // banner (default)
  return (
    <div
      data-testid={testId}
      className={`rounded-xl border px-4 py-3 flex items-start gap-3 ${
        isDark
          ? "border-amber-500/40 bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/5 text-amber-100"
          : "border-amber-300 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 text-amber-900"
      } ${className}`}
    >
      <div className={`shrink-0 w-9 h-9 rounded-lg grid place-items-center ${
        isDark ? "bg-amber-500/25 text-amber-200" : "bg-amber-100 text-amber-700"
      }`}>
        <GraduationCap className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-extrabold uppercase tracking-wide leading-tight">
          {t.tag}
        </div>
        <p className="text-xs sm:text-[13px] leading-snug mt-0.5">{t.body}</p>
      </div>
    </div>
  );
}
